import type {
  CustomerCriteria,
  CustomerDTO,
  CustomerTransactionDTO,
  CustomerUpdateDTO,
  MembershipInfoCriteria,
  MembershipTypeV2,
  PaginationDTO,
  RefundReason,
  TransactionRefundRequest,
  TransactionRefundType,
  ViolationCriteria,
} from '@/api/generated/types';

/** Search form values by field, as a screen's `searchFrom` builds them from its URL filters. */
export type SearchValues = Record<string, unknown>;

/*
 * Domain vocabulary for customers. Numeric codes are the backend's (veo-customer); the
 * generated contract types them as plain numbers, so the labels live here.
 */

export const CUSTOMER_STATUS: Record<number, { text: string; color: string }> = {
  0: { text: 'Normal', color: 'green' },
  1: { text: 'Frozen', color: 'blue' },
  2: { text: 'Error', color: 'red' },
  3: { text: 'Suspended', color: 'orange' },
};

export const EMAIL_STATUS: Record<number, string> = { 0: 'Unverified', 1: 'Verified', 2: 'Educational' };

export const TRANSACTION_TYPE: Record<number, string> = {
  0: 'Admin adjustment',
  1: 'Membership refund',
  2: 'Admin refund',
  3: 'Membership purchase',
  4: 'Pickup fee',
  5: 'Promo',
  6: 'Migration',
  7: 'Ride',
  8: 'Deposit',
  9: 'Dispute freeze',
  10: 'Violation fine',
  11: 'Violation revert',
  12: 'Shop order',
  13: 'Auto reload',
  14: 'Hold captured',
  15: 'Deposit refund',
  16: 'Hold captured refund',
  17: 'Shop order refund',
  18: 'Ride pass purchase',
  19: 'Ride pass refund',
};

export const MEMBERSHIP_TYPES: { value: MembershipTypeV2; label: string }[] = [
  { value: 'STANDARD', label: 'Premium' },
  { value: 'COMMUTER', label: 'Commuter' },
  { value: 'PLATINUM', label: 'Platinum' },
  { value: 'ENTERPRISE', label: 'Enterprise' },
  { value: 'VEO_ACCESS', label: 'Veo Access' },
  { value: 'RIDE_PASS', label: 'Ride pass' },
];

export const PASS_STATUS: Record<string, { text: string; color: string }> = {
  active: { text: 'Active', color: 'green' },
  used: { text: 'Used up', color: 'default' },
  expired: { text: 'Expired', color: 'default' },
};

/** Veo Access (Denver low-income program) enrolment states; the backend matches case-insensitively. */
export const ACCESS_STATUS = ['pending', 'verified', 'expired', 'optout'] as const;

export const VIOLATION_STATUS: Record<number, { text: string; color: string }> = {
  0: { text: 'Waiting for review', color: 'gold' },
  1: { text: 'Rejected', color: 'default' },
  2: { text: 'Approved', color: 'red' },
  3: { text: 'Reverted', color: 'purple' },
  4: { text: 'Void', color: 'default' },
};

export const NOTIFICATION_CHANNEL: Record<number, string> = { 1: 'SMS', 2: 'Push', 3: 'Email' };

/** Customer-membership rows of POST /customers/memberships/search/v2 (not in the generated contract). */
export interface CustomerMembershipInfo {
  experimentVariantValue?: string | null;
  id?: string | null;
  areaMembershipId?: string | null;
  name?: string | null;
  type?: MembershipTypeV2 | null;
  expirationTime?: string | null;
  renewable?: boolean | null;
  customer?: { id?: number | null; phone?: number | null; countryCode?: number | null; fullName?: string | null; email?: string | null } | null;
  areaId?: number | null;
  snapshotAreaId?: number | null;
  platform?: string | null;
}

/** Purchase history from POST /customers/{id}/memberships/search; `id` is a history record, never a v2 action target. */
export interface CustomerMembershipHistory {
  experimentVariantValue?: string | null;
  id?: string | null;
  membershipId?: string | null;
  membershipName?: string | null;
  buyMethod?: string | null;
  startTime?: string | null;
  expirationTime?: string | null;
  renewTime?: string | null;
  areaId?: number | null;
  activated?: boolean | null;
}

/** Keeps the first character and the domain: "j•••@veoride.com". */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return '—';
  const at = email.indexOf('@');
  if (at <= 0) return '•••';
  return `${email[0]}•••${email.slice(at)}`;
}

/** Tokens and processor ids: only the last 4 characters are ever rendered. */
export function maskSecret(value: string | null | undefined): string {
  if (!value) return '—';
  return value.length <= 4 ? '••••' : `••••${value.slice(-4)}`;
}

/** "+1 5550001" for detail views (lists use maskPhone). */
export function fullPhone(c: { phone?: number | null; countryCode?: number | null }): string {
  return c.phone ? `+${c.countryCode ?? 1} ${c.phone}` : '—';
}

/** Digits of a phone field ("(555) 000-1234" -> 5550001234); undefined when empty. */
export function parsePhone(v: unknown): number | undefined {
  const digits = String(v ?? '').replace(/\D/g, '');
  return digits ? Number(digits) : undefined;
}

/** Search form -> customer search criteria for one (0-based) page, scoped to the working market. */
export function buildCustomerCriteria(params: SearchValues, areaIds: number[] | undefined, pagination: PaginationDTO): CustomerCriteria {
  const range = params.created as [string, string] | undefined;
  return {
    phone: parsePhone(params.phone),
    countryCode: params.phone ? Number(params.countryCode ?? 1) : undefined,
    email: (params.email as string)?.trim() || undefined,
    ids: params.id ? [Number(params.id)] : undefined,
    status: params.status === undefined || params.status === '' ? undefined : Number(params.status),
    ageValidated: optionalBool(params.ageValidated),
    timeRange: range ? { start: range[0], end: range[1] } : undefined,
    areaIds,
    pagination,
  };
}

/** "true"/"false" select values -> boolean; empty -> no filter. */
export const optionalBool = (v: unknown) => (v === undefined || v === '' ? undefined : v === 'true');

/** v2 customer-membership search in one market; most recently changed first. */
export function buildMembershipCriteria(params: SearchValues, areaId: number | undefined, pagination: PaginationDTO): MembershipInfoCriteria {
  const range = params.expirationTime as [string, string] | undefined;
  const types = params.type as MembershipTypeV2[] | undefined;
  return {
    areaId,
    phone: parsePhone(params.phone),
    countryCode: params.phone ? Number(params.countryCode ?? 1) : undefined,
    membershipTypes: types?.length ? types : undefined,
    renewable: optionalBool(params.renewable),
    activated: optionalBool(params.activated),
    expiration: range ? { start: range[0], end: range[1] } : undefined,
    pagination: { ...pagination, sort: { direction: 'desc', sortBy: 'updated' } },
  };
}

const optionalNum = (v: unknown) => (v === undefined || v === null || v === '' ? undefined : Number(v));

/** Violation review queue search (cursor-paged), scoped to the working market. */
export function buildViolationCriteria(params: SearchValues, areaIds: number[] | undefined, cursor: number | null, pageSize = 20): ViolationCriteria {
  const range = params.created as [string, string] | undefined;
  return {
    areaIds,
    phone: parsePhone(params.phone),
    countryCode: params.phone ? Number(params.countryCode ?? 1) : undefined,
    customerId: optionalNum(params.customerId),
    rideId: optionalNum(params.rideId),
    vehicleNumber: optionalNum(params.vehicleNumber),
    status: optionalNum(params.status),
    timeRange: range ? { start: range[0], end: range[1] } : undefined,
    cursor,
    pageSize,
  };
}

/** Wallet total shown in lists: ride credit + deposit. */
export function walletTotal(c: CustomerDTO): number {
  return (c.rideCredit ?? 0) + (c.deposit ?? 0);
}

/** What a transaction moved, most relevant figure first, for the refund default. */
export function refundableAmount(t: CustomerTransactionDTO): number {
  const candidates = [t.paymentCharge, t.holdCapture, t.depositChange, t.rideCreditChange];
  const v = candidates.find((x) => x != null && x !== 0);
  return Math.abs(v ?? 0);
}

export interface RefundForm {
  type: TransactionRefundType;
  amount: number;
  refundReason: RefundReason;
  customerFault?: boolean;
  note?: string;
}

export function buildRefundRequest(t: CustomerTransactionDTO, f: RefundForm): TransactionRefundRequest {
  return {
    type: f.type,
    amount: Math.round(f.amount * 100) / 100,
    refundReason: f.refundReason,
    customerFault: !!f.customerFault,
    note: f.note?.trim() || undefined,
    groupId: t.groupId ?? undefined,
  };
}

/** Signed balance adjustment; both zero is not an adjustment. */
export function buildBalanceAdjustment(v: { rideCredit?: number | null; deposit?: number | null }) {
  const rideCredit = v.rideCredit || 0;
  const deposit = v.deposit || 0;
  if (rideCredit === 0 && deposit === 0) return null;
  return { rideCredit: rideCredit || undefined, deposit: deposit || undefined };
}

/** Editable profile fields; PATCH sends only what changed so concurrent edits are not clobbered. */
export const PROFILE_FIELDS = [
  'fullName',
  'email',
  'emailStatus',
  'status',
  'areaId',
  'ageValidated',
  'lowIncome',
  'autoReloaded',
  'isEducationModeActivated',
  'notes',
] as const satisfies readonly (keyof CustomerUpdateDTO & keyof CustomerDTO)[];

export type ProfileForm = Pick<CustomerUpdateDTO, (typeof PROFILE_FIELDS)[number]>;

export function profileChanges(original: CustomerDTO, values: ProfileForm): CustomerUpdateDTO {
  const out: Record<string, unknown> = {};
  for (const k of PROFILE_FIELDS) {
    const before = original[k] ?? null;
    let after: unknown = values[k] ?? null;
    if (typeof after === 'string') after = after.trim();
    // null fields are ignored by the PATCH, so a cleared text field is sent as "".
    if (after === '' && before === null) continue;
    if (after !== null && after !== before) out[k] = after;
  }
  return out as CustomerUpdateDTO;
}

/** The customer notes limit (CustomerUpdateDTO.notes @Size(max = 512)). */
export const NOTES_MAX_LENGTH = 512;

/**
 * Customer notes with the deletion audit line appended, as the legacy console wrote it; older
 * notes are trimmed from the end so the whole fits the notes limit.
 */
export function withDeletionNote(notes: string | null | undefined, admin: { phone?: number | null; email?: string | null }, ticket: number, at: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const when = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`;
  const line = `Deleted by Manhattan user: ${admin.phone ?? ''}, ${admin.email ?? ''}, ${when}, based on Zendesk ticket #${ticket}`;
  const current = notes ?? '';
  if (!current) return line.slice(0, NOTES_MAX_LENGTH);
  if (current.length + line.length + 1 <= NOTES_MAX_LENGTH) return `${current}\n${line}`;
  return `${current.slice(0, Math.max(0, NOTES_MAX_LENGTH - line.length - 1))}\n${line}`;
}
